import React, { useState, useCallback, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import Error from 'components/error'
import { coachColorAt } from './academy-resource-calendar'
import AcademyCoachSelector from './academy-coach-selector'
import AcademyScheduleNavigation from './academy-schedule-navigation'
import AcademyScheduleDataCalendar from './academy-schedule-data-calendar'
import AcademyCoachSettingsPanel from './academy-coach-settings-panel'
import AcademyScheduleEventDialog from './academy-schedule-event-dialog'
import ScheduleQuickEditEventDialog from '../schedule-quick-edit-event-dialog'
import { useAcademyCoaches } from './use-academy-coach-schedules'
import { isGoogleBusyTime } from './academy-schedule-event-utils'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 200px)',
    minHeight: 480,
    overflow: 'hidden',
  },
  calendarSlot: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  coachBar: {
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    flexShrink: 0,
  },
}))

function AcademyScheduleView({ academyId }) {
  const classes = useStyles()

  const rangeSetterRef = useRef(null)
  const loadingSetterRef = useRef(null)

  const handleRangeChange = useCallback(range => {
    rangeSetterRef.current?.(range)
  }, [])

  const handleRegisterRangeHandler = useCallback(setter => {
    rangeSetterRef.current = setter
  }, [])

  const handleRegisterLoadingHandler = useCallback(setter => {
    loadingSetterRef.current = setter
  }, [])

  const handleLoadingChange = useCallback(loading => {
    loadingSetterRef.current?.(loading)
  }, [])

  const [selectedCoachIds, setSelectedCoachIds] = useState(new Set())
  const [settingsPanelCoach, setSettingsPanelCoach] = useState(null)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [selectedEventCoach, setSelectedEventCoach] = useState(null)
  const [quickEditSlug, setQuickEditSlug] = useState(null)
  const [coachStates, setCoachStates] = useState({})

  const { academy, allCoaches, coachesLoading, error } = useAcademyCoaches(
    academyId,
  )

  const didInitCoachSelectionRef = React.useRef(false)
  React.useEffect(() => {
    if (didInitCoachSelectionRef.current) return
    if (allCoaches.length === 0) return
    didInitCoachSelectionRef.current = true
    setSelectedCoachIds(new Set(allCoaches.map(c => c.id)))
  }, [allCoaches])

  const selectedCoaches = useMemo(
    () => allCoaches.filter(c => selectedCoachIds.has(c.id)),
    [allCoaches, selectedCoachIds],
  )

  const coachColorMap = useMemo(() => {
    const map = {}
    allCoaches.forEach((coach, idx) => {
      map[coach.id] = coachColorAt(idx)
    })
    return map
  }, [allCoaches])

  const handleToggleCoach = useCallback(coachId => {
    setSelectedCoachIds(prev => {
      const next = new Set(prev)
      if (next.has(coachId)) {
        next.delete(coachId)
      } else {
        next.add(coachId)
      }
      return next
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    setSelectedCoachIds(new Set(allCoaches.map(c => c.id)))
  }, [allCoaches])

  const handleDeselectAll = useCallback(() => {
    setSelectedCoachIds(new Set())
  }, [])

  const settingsPanelIndex = useMemo(
    () =>
      settingsPanelCoach
        ? allCoaches.findIndex(c => c.id === settingsPanelCoach.id)
        : 0,
    [settingsPanelCoach, allCoaches],
  )

  const coachById = useMemo(() => {
    const map = {}
    for (const coach of allCoaches) {
      map[coach.id] = coach
    }
    return map
  }, [allCoaches])

  const handleEventClick = useCallback(
    (occurrence, coachId) => {
      if (!occurrence || isGoogleBusyTime(occurrence)) return
      setSelectedEvent(occurrence)
      setSelectedEventCoach(coachById[coachId] || null)
    },
    [coachById],
  )

  const closeEventDialog = useCallback(() => {
    setSelectedEvent(null)
    setSelectedEventCoach(null)
  }, [])

  const handleEditGroupEvent = useCallback(
    slug => {
      setQuickEditSlug(slug)
      closeEventDialog()
    },
    [closeEventDialog],
  )

  if (!academyId) {
    return (
      <Box p={3}>
        <Typography color="textSecondary">
          No academy found for your account.
        </Typography>
      </Box>
    )
  }

  if (error) return <Error error={error} />

  return (
    <Box className={classes.root}>
      <Box className={classes.calendarSlot}>
        <AcademyScheduleNavigation
          academyName={academy?.name}
          onRangeChange={handleRangeChange}
          onRegisterLoadingHandler={handleRegisterLoadingHandler}
        >
          <AcademyScheduleDataCalendar
            academyId={academyId}
            coaches={selectedCoaches}
            coachColorMap={coachColorMap}
            onEventClick={handleEventClick}
            onRegisterRangeHandler={handleRegisterRangeHandler}
            onLoadingChange={handleLoadingChange}
            onCoachStatesChange={setCoachStates}
          />
        </AcademyScheduleNavigation>
      </Box>

      <Box className={classes.coachBar}>
        <AcademyCoachSelector
          coaches={allCoaches}
          coachColorMap={coachColorMap}
          coachStates={coachStates}
          loading={coachesLoading}
          selectedCoachIds={selectedCoachIds}
          onToggleCoach={handleToggleCoach}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          onShowSettings={coach => setSettingsPanelCoach(coach)}
        />
      </Box>

      <AcademyCoachSettingsPanel
        open={!!settingsPanelCoach}
        coach={settingsPanelCoach}
        coachIndex={settingsPanelIndex}
        onClose={() => setSettingsPanelCoach(null)}
      />

      <AcademyScheduleEventDialog
        open={!!selectedEvent}
        occurrence={selectedEvent}
        coach={selectedEventCoach}
        onClose={closeEventDialog}
        onEditGroupEvent={handleEditGroupEvent}
      />

      <ScheduleQuickEditEventDialog
        open={!!quickEditSlug}
        slug={quickEditSlug}
        onClose={() => setQuickEditSlug(null)}
      />
    </Box>
  )
}

AcademyScheduleView.propTypes = {
  academyId: PropTypes.string,
}

export default AcademyScheduleView
